
import { defineComponent, SetupContext, ref, computed, PropType } from "vue";
import NativeClient from "@/client/native-client";
import { PantryPlateSettingWeightData, PLATE_DISPLAY_FORMAT } from "@/types/pantry";
import DateUtil from "@/utils/date-util";

export default defineComponent({
  name: "PantryDetailGraphView",
  props: {
    weightData: {
      type: PantryPlateSettingWeightData,
      default: null
    }
  },
  setup(props, context: SetupContext) {
    const baseCanvasSize = { width: 320, height: 160 };

    // グラフ描画
    const canvas = ref<HTMLCanvasElement>();
    const drawGraph = () => {
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph start");
      if (props.weightData == null || props.weightData.info == null || props.weightData.info.weights == null || props.weightData.info.unit == null) {
         NativeClient.sendDebugLog("PantryDetailGraphView drawGraph data empty");
        return;
      }
      const valueList = props.weightData.info.weights;
      // 実行判定
      if (valueList.length == 0) {
        NativeClient.sendDebugLog("PantryDetailGraphView drawGraph list no element");
        return;
      }
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph info = " + JSON.stringify(props.weightData.info));
      if (canvas.value === undefined) {
        NativeClient.sendDebugLog("PantryDetailGraphView drawGraph canvas.value is undefined");
        return;
      }

      const targetContext = canvas.value.getContext("2d");
      if (targetContext == null) {
        NativeClient.sendDebugLog("PantryDetailGraphView drawGraph targetContext is null");
        return;
      }
      const screenScale = window.devicePixelRatio;
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph screenScale = " + screenScale);
      const realCanvasSize = { width: baseCanvasSize.width * screenScale, height: baseCanvasSize.height * screenScale };
      targetContext.clearRect(0, 0, realCanvasSize.width, realCanvasSize.height);
      const graphAreaSize = { width: 256 * screenScale, height: 116 * screenScale };
      const originPosition = { x: 40 * screenScale, y: 16 * screenScale };
      let maxValue = props.weightData.info.maxValue;
      if (props.weightData.info.unit.displayFormat && props.weightData.info.unit.displayFormat == PLATE_DISPLAY_FORMAT.GAUGE) {
        maxValue = 100;
      }
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph maxValue = " + maxValue);

      // 定数設定
      const intervalXAxis = graphAreaSize.width / 6;
      const scaleLineWidth = screenScale;
      const scaleStrokeColor = "#707070";
      const strokeWidth = 2 * screenScale;
      const strokeColor = "#14A5B8";

      // 両端Y軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(originPosition.x, originPosition.y + graphAreaSize.height);
      targetContext.strokeStyle = scaleStrokeColor;
      targetContext.lineWidth = scaleLineWidth;
      targetContext.stroke();
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph Y axis start = (" + originPosition.x + ", " + originPosition.y + "), end = (" + originPosition.x + ", " + 0 + ")");
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x + graphAreaSize.width, originPosition.y);
      targetContext.lineTo(originPosition.x + graphAreaSize.width, originPosition.y + graphAreaSize.height);
      targetContext.strokeStyle = scaleStrokeColor;
      targetContext.lineWidth = scaleLineWidth;
      targetContext.stroke();
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph Y axis start = (" + graphAreaSize.width + ", " + originPosition.y + "), end = (" + graphAreaSize.width + ", " + 0 + ")");

      // X軸と並行な線
      const intervalHeight = graphAreaSize.height / 4;
      let currentYposition = originPosition.y;
      for (let i = 0; i < 5; i ++) {
        targetContext.beginPath();
        targetContext.moveTo(originPosition.x, currentYposition);
        targetContext.lineTo(originPosition.x + graphAreaSize.width, currentYposition);
        targetContext.strokeStyle = scaleStrokeColor;
        targetContext.lineWidth = scaleLineWidth;
        targetContext.stroke();
        NativeClient.sendDebugLog("PantryDetailGraphView drawGraph X Line yPos = " + currentYposition);
        currentYposition += intervalHeight;
      }

      // 開始曜日を設定
      const start = new Date();
      start.setDate(start.getDate() - 6);
      // 曜日 （先頭の曜日だけ決定して、7日間を設定する）
      const dayOfWeekString = [ "日", "月", "火", "水", "木", "金", "土" ]
      let currentDayOfWeekPosition = originPosition.x;
      let currentDayOfWeek = start.getDay();
      for (let i = 0; i < dayOfWeekString.length; i++) {
        targetContext.font = `${11 * screenScale}px sans-serif`;
        targetContext.textAlign = "center";
        targetContext.textBaseline = "middle";
        switch (currentDayOfWeek) {
          case 0:   // 日曜
            targetContext.fillStyle = "#E18075";
            break;
          case 6:   // 土曜日
            targetContext.fillStyle = "#67A2D5";
            break;
          default:
            targetContext.fillStyle = "#777777";
            break;
        }
        targetContext.fillText(dayOfWeekString[currentDayOfWeek], currentDayOfWeekPosition, originPosition.y + graphAreaSize.height + 10 * screenScale);
        currentDayOfWeekPosition += intervalXAxis;
        currentDayOfWeek += 1;
        if (currentDayOfWeek >= 7) {
          currentDayOfWeek = 0;
        }
      }
      const startDate = DateUtil.dateYYYYMMDD(start);
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph start = " + startDate);
      const newValueList = new Array<number>();
      let dayDate = startDate;
      for (let i = 0; i < 7; i++) {
        let value = 0;
        NativeClient.sendDebugLog("PantryDetailGraphView drawGraph dayDate = " + dayDate);
        for (let j = 0; j < valueList.length; j++) {
          const datetime = valueList[j].datetime;
          const timezoneDate = DateUtil.getFormattedDateYYYYMMDDWithTimezone(datetime);
          // NativeClient.sendDebugLog("PantryDetailGraphView drawGraph timezoneDate = " + timezoneDate);
          if (dayDate == timezoneDate) {
            NativeClient.sendDebugLog("PantryDetailGraphView drawGraph datetime = " + timezoneDate + ", timezone = "+ DateUtil.getFormattedDateWithTimezone(datetime));
            value = valueList[j].weight;
            break;
          }
        }
        if (value > maxValue) {
          value = maxValue;
        }
        newValueList.push(value);
        const date = new Date(dayDate);
        date.setDate(date.getDate() + 1);
        dayDate = DateUtil.dateYYYYMMDD(date);
      }

      // 描画
      let currentXPosition = originPosition.x;
      targetContext.beginPath();
      const yPosition = originPosition.y + (maxValue - newValueList[0]) / maxValue * graphAreaSize.height;
      targetContext.moveTo(currentXPosition, yPosition);
      NativeClient.sendDebugLog("PantryDetailGraphView drawGraph Y Position = " + yPosition + ", value = " + newValueList[0]);
      for (let i = 1; i < newValueList.length; i++) {
        currentXPosition += intervalXAxis;
        const weight = newValueList[i];
        const value = weight > 100 ? 100 : weight;
        const currentYPosition = originPosition.y + (maxValue - value) / maxValue * graphAreaSize.height;
        NativeClient.sendDebugLog("PantryDetailGraphView drawGraph Y Position = " + currentYPosition + ", weight = " + weight+ ", value =" + value);
        targetContext.lineTo(currentXPosition, currentYPosition);
      }
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = strokeWidth;
      targetContext.stroke();

      // MAX
      targetContext.font = `${12 * screenScale}px sans-serif`;
      targetContext.fillStyle = "#777777";
      targetContext.textAlign = "left";
      targetContext.textBaseline = "top";
      targetContext.fillText("MAX", 0, 3 * screenScale);

      // 0
      targetContext.font = `${11 * screenScale}px sans-serif`;
      targetContext.fillStyle = "#777777";
      targetContext.textAlign = "left";
      targetContext.textBaseline = "middle";
      targetContext.fillText("0", originPosition.x - 16 * screenScale, originPosition.y + graphAreaSize.height);
    };

    const canvasSize = computed(() => {
      const scale = window.devicePixelRatio;
      NativeClient.sendDebugLog("PantryDetailGraphView devicePixelRatio = " + scale);
      return { width: baseCanvasSize.width * scale, height: baseCanvasSize.height * scale };
    });

    // 画面が小さい場合の調整
    const styleCanvasArea = computed(() => {
      if (window.innerWidth <= 320) {
        const scale = (window.innerWidth - 32) / 320;
        return { 'transform': `scale(${scale})` };
      }
      return { 'transform': 'scale(1.0)' };
    });

    const drawScreen = computed(async () => {
      if (props.weightData) {
        drawGraph();
      }
    });

    return {
      canvas,
      canvasSize,
      styleCanvasArea,
      drawScreen
    };
  }
});
